import {
    Coordinate,
    Entity,
    EntityId,
    getCoordinateDistance,
    getDistance,
    getEntityDistance,
    Latch,
    MovementInertiaConfig,
    Orientation,
    randomIntBetween,
    TileSize,
} from "game-common/models"
import { logger } from "./logger"
import {
    blankCoordinate,
    calculateNewCoordinateFromRadians,
    calculatePointAtDistance,
    Callback,
    clearCoordinate,
    degrees_to_radians,
    emptyCoordinate,
    fineToRough,
    fineToRoughCoordinates,
    floorCoordinates,
    getAngle,
    isEqual,
    radians_to_degrees,
    roughToFine,
    roughToFineCoordinates,
    roundCoordinates,
} from "game-common/util"

import { InteractionDescriptor, resolveInteractions } from "game-common/mechanics/entity_mechanics"

import { Client } from "./client"
import { IKeyboard, IMouse, KeyModifiers, MouseState } from "./client_models"
import { isMobile, ScalingParams } from "./client_util"
import { MapRenderingManager } from "./rendering/impl/pixijs/map_rendering_manager"
import { Fader } from "./rendering/impl/pixijs/gui/fader"
import { MapEntry } from "game-common/room/base_map_builder"
import { Sprite } from "pixi.js"

// How long a press has to sit on an interactable entity before it stops meaning "interact" and starts
// meaning "attack". Short enough that holding to swing feels immediate; comfortably longer than any
// deliberate tap, including a touch tap, which is what keeps the two gestures from being confused.
const ENTITY_PRESS_HOLD_MS = 220

// An armed location pick: the caption shown under the reticle, and what to do with the fine world
// coordinate the player settles on. See PlayerController.beginLocationPick.
export interface LocationPickRequest {
    // The line under the reticle, and what replaces it over ground nothing can stand on.
    caption: string
    blockedCaption: string
    onPick: (point: Coordinate) => void
}

class DegreesRotator {
    targetAngle: number
    increment: number
    currentAngle: number

    constructor(targetAngle: number, increment: number, currentAngle: number) {
        this.targetAngle = targetAngle
        this.currentAngle = currentAngle
        this.increment = increment

        const delta = this.targetAngle - this.currentAngle

        if (Math.abs(delta) >= 180) {
            if (this.targetAngle > this.currentAngle) {
                this.currentAngle += 360
            } else {
                this.targetAngle += 360
            }
        }
    }

    update = (): boolean => {
        const delta = this.targetAngle - this.currentAngle
        if (delta < 0) {
            this.currentAngle -= this.increment
        } else {
            this.currentAngle += this.increment
        }

        if (Math.abs(delta) < this.increment) {
            return true
        }
        return false
    }

    normalizedCurrent = () => {
        return this.currentAngle > 360 ? this.currentAngle - 360 : this.currentAngle
    }

    normalizedTarget = () => {
        return this.targetAngle > 360 ? this.targetAngle - 360 : this.targetAngle
    }
}

export class Player {
    client: Client
    entity: Entity
    private mouseHandler: IMouse
    private keyboardHandler: IKeyboard
    private isChat: boolean = false
    private lastMouseMoveTs: number = 0
    private keyboardActionEnabled: boolean = true
    private mouseActionEnabled: boolean = true
    private suppressAttackUntilTs: number = 0
    private paralyzed: boolean
    // DOWNED: the server's verdict that this player has fallen and has not been put back on their
    // feet yet (Message.downed, mirrored by ClientGameLogic.updateFromPlayerMessage). Distinct
    // from `paralyzed`, which is a short client-side beat that times itself out -- this one lasts
    // as long as the server says it does, which for an ember camp is however long the player takes
    // to answer their end-of-run panel. The server refuses their input for the same reason, so
    // stopping here is what keeps them from predicting a walk they never get to take.
    private downed: boolean = false
    private randomness: any = undefined
    private isFiring: boolean
    private fireInterval: any
    private guest: boolean
    private targetKeyboardAngle: number
    private keyboardAndleAdjustInterval: any
    private pointer: Coordinate
    private roughPointer: Coordinate
    private roughCompositeTilePointer: Coordinate = blankCoordinate()
    private movementDaemon: any
    private longRangedEquipped: boolean = undefined
    private mousePointerFadeInterval: any
    private mousePointerFader: Fader
    private terrainPointerOn: boolean = false
    private terrainPointerSprites: Set<Sprite> = new Set()
    // LOCATION PICK: an armed "click a spot on the ground" gesture (the defender's "Hold Position"
    // order). While it is set the pointer means "here" rather than "attack" -- every world press,
    // every entity press and every build commit is held back for it, and the click that lands
    // answers it instead. Cleared by the pick itself, by Escape, or by anything that takes the
    // player's hands off the world (downed, paralyzed, room change).
    private locationPick?: LocationPickRequest
    private isAutoMoving: boolean
    private hoveredEntity: EntityId
    // An in-flight press on an entity that offers interactions, and whether it has already crossed
    // the hold threshold and turned into an attack. See beginEntityPress.
    private pressedEntityId?: EntityId
    private pressBecameAttack: boolean = false
    private pressHoldTimer?: ReturnType<typeof setTimeout>
    private autoMovingInterval: any
    private velocityUpdateInterval: any
    private targetSpeed2: Coordinate = { x: 0, y: 0 }
    // Held movement intent from the mobile joysticks. Their onChange only fires on pointermove,
    // so a thumb parked at full deflection produces no events at all -- the intent has to be
    // latched here and re-applied by updatePlayerVelocity every tick, exactly as a held key is.
    private joystickSpeed2: Coordinate | null = null
    private movementStartTime: number = 0
    private isSustainedMovement: boolean = false

    constructor(client: Client, entity: Entity, mouseHandler: IMouse, keyboardHandler: IKeyboard, guest: boolean) {
        this.client = client
        this.entity = entity
        this.mouseHandler = mouseHandler
        this.keyboardHandler = keyboardHandler
        this.guest = guest

        keyboardHandler.addListener(this.respondToKeyBoard)
        keyboardHandler.addPasteListener(this.respondToPaste)
        mouseHandler.addListener(this.respondToMouse)

        // Start velocity update loop
        this.startVelocityUpdates()
    }

    private isKeyOn = (key: string) => {
        // If in chat mode, don't detect movement keys
        if (this.isChat && ["w", "a", "s", "d", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(key)) {
            return false
        }
        return this.keyboardHandler.state().get(key) || this.keyboardHandler.state().get(key.toUpperCase())
    }
    private isButtonOn = (key: string) => this.mouseHandler.state().buttons.get(key) == true

    adjustEntityAngle = (entity: Entity, delta: Coordinate) =>
        getAngle(
            { x: entity.location.x, y: entity.location.y },
            { x: entity.location.x + delta.x, y: entity.location.y + delta.y },
        )

    private keyboardBasedAngle = (entity: Entity) => {
        const leftRightOn =
            this.isKeyOn("a") || this.isKeyOn("ArrowLeft") || this.isKeyOn("d") || this.isKeyOn("ArrowRight")
        const upDownOn = this.isKeyOn("w") || this.isKeyOn("ArrowUp") || this.isKeyOn("s") || this.isKeyOn("ArrowDown")

        let newTargetAngle

        if (!leftRightOn) {
            if (this.isKeyOn("w") || this.isKeyOn("ArrowUp")) {
                newTargetAngle = this.adjustEntityAngle(entity, { x: 0, y: 1 })
            }
            if (this.isKeyOn("s") || this.isKeyOn("ArrowDown")) {
                newTargetAngle = this.adjustEntityAngle(entity, { x: 0, y: -1 })
            }
        } else {
            if (this.isKeyOn("w") || this.isKeyOn("ArrowUp")) {
                if (this.isKeyOn("a") || this.isKeyOn("ArrowLeft")) {
                    newTargetAngle = this.adjustEntityAngle(entity, { x: 1, y: 1 })
                }
                if (this.isKeyOn("d") || this.isKeyOn("ArrowRight")) {
                    newTargetAngle = this.adjustEntityAngle(entity, { x: -1, y: 1 })
                }
            }
            if (this.isKeyOn("s") || this.isKeyOn("ArrowDown")) {
                if (this.isKeyOn("a") || this.isKeyOn("ArrowLeft")) {
                    newTargetAngle = this.adjustEntityAngle(entity, { x: 1, y: -1 })
                }
                if (this.isKeyOn("d") || this.isKeyOn("ArrowRight")) {
                    newTargetAngle = this.adjustEntityAngle(entity, { x: -1, y: -1 })
                }
            }
        }

        if (!upDownOn) {
            if (this.isKeyOn("a") || this.isKeyOn("ArrowLeft")) {
                newTargetAngle = this.adjustEntityAngle(entity, { x: 1, y: 0 })
            }
            if (this.isKeyOn("d") || this.isKeyOn("ArrowRight")) {
                newTargetAngle = this.adjustEntityAngle(entity, { x: -1, y: 0 })
            }
        } else {
            if (this.isKeyOn("a") || this.isKeyOn("ArrowLeft")) {
                if (this.isKeyOn("w") || this.isKeyOn("ArrowUp")) {
                    newTargetAngle = this.adjustEntityAngle(entity, { x: 1, y: 1 })
                }
                if (this.isKeyOn("s") || this.isKeyOn("ArrowDown")) {
                    newTargetAngle = this.adjustEntityAngle(entity, { x: 1, y: -1 })
                }
            }
            if (this.isKeyOn("d") || this.isKeyOn("ArrowRight")) {
                if (this.isKeyOn("w") || this.isKeyOn("ArrowUp")) {
                    newTargetAngle = this.adjustEntityAngle(entity, { x: -1, y: 1 })
                }
                if (this.isKeyOn("s") || this.isKeyOn("ArrowDown")) {
                    newTargetAngle = this.adjustEntityAngle(entity, { x: -1, y: -1 })
                }
            }
        }

        if (newTargetAngle !== undefined && newTargetAngle !== this.targetKeyboardAngle) {
            if (this.keyboardAndleAdjustInterval) {
                clearInterval(this.keyboardAndleAdjustInterval)
            }
            this.targetKeyboardAngle = newTargetAngle

            const increment = 5

            const rotator = new DegreesRotator(
                Math.floor(radians_to_degrees(newTargetAngle)),
                increment,
                Math.floor(radians_to_degrees(this.entity.movement.angle)),
            )

            this.keyboardAndleAdjustInterval = setInterval(() => {
                const done = rotator.update()
                this.entity.movement.angle = degrees_to_radians(rotator.normalizedCurrent())
                if (done) {
                    this.entity.movement.angle = degrees_to_radians(rotator.normalizedTarget())
                    clearInterval(this.keyboardAndleAdjustInterval)
                }
            }, 5)
        }
    }

    updateRoom = () => {
        // A pick armed in the room just left has nothing to point at any more.
        this.cancelLocationPick()

        if (this.pointer) {
            this.pointer = undefined
        }

        if (this.roughPointer) {
            this.roughPointer = undefined
        }

        this.hoveredEntity = undefined
    }

    updateOrientation = (orientation: Orientation) => {
        const { entity } = this

        switch (orientation) {
            case "south": {
                entity.movement.angle = this.adjustEntityAngle(entity, { x: 0, y: -1 })
                break
            }
            case "north": {
                entity.movement.angle = this.adjustEntityAngle(entity, { x: 0, y: 1 })
                break
            }
            case "east": {
                entity.movement.angle = this.adjustEntityAngle(entity, { x: -1, y: 0 })
                break
            }
            case "west": {
                entity.movement.angle = this.adjustEntityAngle(entity, { x: 1, y: 0 })
                break
            }
        }
    }

    // Pasted text only means anything while the chat input is open; anywhere else in the game
    // there is nothing it could be pasted INTO.
    private respondToPaste = (text: string): void => {
        if (!this.isChat || !this.client.clientGameLogic.isBootstrapped()) {
            return
        }
        this.client.pasteIntoChat(text)
    }

    private respondToKeyBoard = (key: string, down: boolean, repeat?: boolean, mods?: KeyModifiers): void => {
        if (!this.client.clientGameLogic.isBootstrapped()) {
            // can't do anything unless you're bootstrapped
            return
        }
        if (!this.keyboardActionEnabled) {
            return
        }
        const { entity } = this

        if (this.isKeyOn("/") && !this.isChat) {
            this.client.startChat()
            this.isChat = true
        }

        if (this.isKeyOn("Enter")) {
            if (this.isChat) {
                this.client.stopChat()
            } else {
                this.client.startChat()
            }

            this.isChat = !this.isChat
        }

        if (this.isChat && down) {
            this.client.captureKeystroke(key, mods)
        }

        if (this.isKeyOn("Escape") && this.isChat) {
            this.client.stopChat()
            this.isChat = false
        }

        if (this.isChat) {
            return
        }

        // Handle Control key for command wheel
        if (key === "Control" || key === "ControlLeft" || key === "ControlRight") {
            if (down) {
                // Control key pressed - show command wheel
                this.client.clientGameLogic.clientRenderer.gui().showCommandWheel()
            } else {
                // Control key released - hide command wheel
                this.client.clientGameLogic.clientRenderer.gui().hideCommandWheel()
            }
        }

        // Escape dismisses the per-entity command wheel (opened from an NPC's command button,
        // so it isn't tied to holding Control).
        if ((key === "Escape" || key === "Esc") && down) {
            const gui = this.client.clientGameLogic.clientRenderer.gui()
            if (gui.isCommandWheelVisible()) {
                gui.hideCommandWheel()
            }
            // Also dismiss the recruit role picker if it's open.
            if (gui.isRolePickerVisible()) {
                gui.hideRolePicker()
            }
            // ...and either possession splash, which is only ever telling the player something, so
            // Escape is simply the keyboard's dismiss button.
            if (gui.isPossessionSplashVisible()) {
                gui.hidePossessionSplash()
            }
            // ...and the invasion picker, which is open over the end-of-run scorecard while the
            // player is still DOWNED -- so this has to sit above the paralyzed/downed guard below,
            // where it already does. Routed through cancelInvasionPicker so the keyboard way out
            // tells the server exactly what the footer button and the scrim do.
            if (gui.isInvasionPickerVisible()) {
                gui.cancelInvasionPicker()
            }
            // ...and the name prompt. Escape is the one key that still reaches here while a text
            // field holds the keyboard (Keyboard.suppressTyping), precisely so that a field whose
            // focus has been clicked away can still be dismissed.
            if (gui.isNamePromptVisible()) {
                gui.hideNamePrompt()
            }
            // Escape backs out of an armed placement ("Place" on a buildable item): drop the preview
            // right away so the pointer stops meaning "build here", and put the item away server-side,
            // which is what actually ends build mode (INACTIVATE_ITEM -> sendBuilderAction(false)).
            if (this.terrainPointerOn) {
                this.updateTerrainPointerOn(false)
                this.client.clientGameLogic.cancelBuildPlacement()
            }
            // Escape backs out of an armed location pick (the defender's "Hold Position") the same
            // way it backs out of build mode: the order is simply never sent.
            this.cancelLocationPick()
            // Also deactivate the ember follow-cam PiP / roster slot.
            gui.dismissFollowCam()
        }

        if (this.paralyzed || this.downed) {
            entity.movement.moving = false
            entity.speed2.x = 0
            entity.speed2.y = 0
            return
        }

        if (Date.now() - this.lastMouseMoveTs > 150) {
            this.keyboardBasedAngle(entity)
        }

        const isMoving =
            this.isKeyOn("w") ||
            this.isKeyOn("ArrowUp") ||
            this.isKeyOn("s") ||
            this.isKeyOn("ArrowDown") ||
            this.isKeyOn("a") ||
            this.isKeyOn("ArrowLeft") ||
            this.isKeyOn("d") ||
            this.isKeyOn("ArrowRight")

        let manualControl = isMoving
        if (isMoving) {
            clearInterval(this.autoMovingInterval)
            this.setAutoMoving(false)
        }

        // Remove immediate velocity zeroing to allow inertial slowdown
        // The updatePlayerVelocity method will handle velocity updates with inertia

        if (manualControl) {
            const wasMoving = entity.movement.moving

            entity.movement.moving = isMoving

            if (!wasMoving && entity.movement.moving) {
                // started moving!
                this.movementStartTime = Date.now()
                this.isSustainedMovement = false
                if (this.movementDaemon !== undefined) {
                    clearInterval(this.movementDaemon)
                }
                this.movementDaemon = setInterval(() => {
                    if (Date.now() - this.lastMouseMoveTs > 150) {
                        this.keyboardBasedAngle(entity)
                    }
                }, 150)
            }
            if (wasMoving && !entity.movement.moving) {
                // stopped moving
                this.movementStartTime = 0
                this.isSustainedMovement = false
                if (this.movementDaemon !== undefined) {
                    clearInterval(this.movementDaemon)
                }
            }

            if (!entity.afk) {
                // Set target speed2 values
                this.targetSpeed2 = { x: 0, y: 0 }

                if (this.isKeyOn("w") || this.isKeyOn("ArrowUp")) {
                    this.targetSpeed2.y = -1
                }
                if (this.isKeyOn("s") || this.isKeyOn("ArrowDown")) {
                    this.targetSpeed2.y = 1
                }
                if (this.isKeyOn("a") || this.isKeyOn("ArrowLeft")) {
                    this.targetSpeed2.x = -1
                }
                if (this.isKeyOn("d") || this.isKeyOn("ArrowRight")) {
                    this.targetSpeed2.x = 1
                }
            } else {
                // If AFK, set target to zero
                this.targetSpeed2 = { x: 0, y: 0 }
            }
        }

        if (this.isKeyOn("e")) {
            if (this.terrainPointerOn) {
                this.client.clientGameLogic.activateWorldPoint(this.pointer, {
                    delete: true,
                })
            }
        }

        if (this.isKeyOn("p")) {
            this.updateTerrainPointerOn(!this.terrainPointerOn)
        }

        if (this.isKeyOn("u")) {
            this.client.spawnNpc()
        }

        if (this.isKeyOn("x")) {
            this.client.dropAction()
        }

        if (this.isKeyOn("y")) {
            this.client.itemPickupAction()
        }

        if (this.isKeyOn("b")) {
            this.activeAbility("b")
        }

        if (this.isKeyOn(" ")) {
            this.manualAttack(isMoving)
        }

        if (this.isKeyOn("q")) {
            this.client.dropActiveItem()
        }

        if (this.isKeyOn("]")) {
            this.client.activateAbility("e")
        }

        if (this.isKeyOn("1")) {
            this.client.clientGameLogic.clientRenderer.gui().activateInventorySlot(1)
        }

        if (this.isKeyOn("2")) {
            this.client.clientGameLogic.clientRenderer.gui().activateInventorySlot(2)
        }

        if (this.isKeyOn("3")) {
            this.client.clientGameLogic.clientRenderer.gui().activateInventorySlot(3)
        }

        if (this.isKeyOn("4")) {
            this.client.clientGameLogic.clientRenderer.gui().activateInventorySlot(4)
        }

        if (this.isKeyOn("5")) {
            this.client.clientGameLogic.clientRenderer.gui().activateInventorySlot(5)
        }

        if (this.isKeyOn("6")) {
            this.client.clientGameLogic.clientRenderer.gui().activateInventorySlot(6)
        }

        if (this.isKeyOn("j")) {
            this.client.emitPlayerEvent({
                playerEventType: "SQUAD_COMMAND",
                context: {
                    command: "come",
                },
            })
        }

        if (this.isKeyOn("h")) {
            this.client.emitPlayerEvent({
                playerEventType: "SQUAD_COMMAND",
                context: {
                    command: "stopStart",
                },
            })
        }

        if (this.isKeyOn("k")) {
            this.client.emitPlayerEvent({
                playerEventType: "SQUAD_COMMAND",
                context: {
                    command: "formation",
                },
            })
        }

        if (this.isKeyOn("l")) {
            this.client.emitPlayerEvent({
                playerEventType: "SQUAD_COMMAND",
                context: {
                    command: "updateStance",
                },
            })
        }

        if (key === "Shift") {
            if (down) {
                this.client.boost(true)
            } else {
                this.client.boost(false)
            }
        }

        if (this.isKeyOn("~")) {
            if (this.randomness) {
                clearInterval(this.randomness)
                this.randomness = null
            } else {
                const latch = new Latch(500)
                this.randomness = setInterval(() => {
                    if (latch.expired()) {
                        const w = randomIntBetween(0, 20)
                        this.client.fireBullet()
                    }
                    const r = randomIntBetween(0, 1)
                    entity.movement.moving = true
                    entity.speed2.x = r * (randomIntBetween(0, 1) === 1 ? -1 : 1)
                    entity.speed2.y = r * (randomIntBetween(0, 1) === 1 ? -1 : 1)
                }, 100)
            }
        }

        if (this.isKeyOn("i")) {
            this.client.clientGameLogic.clientRenderer.gui().toggleInventory()
            // this.client.clientGameLogic.invokeInventory()
        }

        // Toggle faction tracking (offscreen edge arrows). Gate on `down` so holding the
        // key doesn't rapidly flip it. No-op unless the room designated a faction to track.
        if ((key === "f" || key === "F") && down && !this.isChat) {
            this.client.clientGameLogic.toggleFactionTracking()
        }

        // Toggle the crafting screen. Gated on the key's own key-down, and on it not being an
        // auto-repeat, so holding `c` doesn't flip the screen open and shut.
        if ((key === "c" || key === "C") && down && !repeat) {
            this.client.clientGameLogic.clientRenderer.gui().toggleCrafting()
        }

        if (this.isKeyOn("t")) {
            if (entity.debug || entity.name === "aron") {
                this.client.clientGameLogic.clientRenderer.gui().toggleCharacterCustomizer()
            }
            // this.client.clientGameLogic.clientRenderer.gui().showTutorial("hotbar")
        }
    }

    perturb = () => {
        const { entity } = this
        entity.speed2.x = 0.001
        setTimeout(() => {
            entity.speed2.x = 0
        }, 10)
    }

    private respondToMouse = (mouseState: MouseState): void => {
        if (!this.mouseActionEnabled) {
            return
        }
        this.lastMouseMoveTs = Date.now()
        if (!isMobile()) {
            if (!this.pointer && this.client.clientGameLogic.tilemapMetaProvider) {
                const pointer = {
                    x: 64,
                    y: 64,
                }
                this.pointer = pointer
            }
            if (!this.roughPointer && this.client.clientGameLogic.tilemapMetaProvider) {
                const pointer = {
                    x: 0,
                    y: 0,
                }
                this.roughPointer = pointer
            }
            this.client.clientGameLogic.clientRenderer.entity()
            const X_FACTOR = ScalingParams.viewportX
            const Y_FACTOR = ScalingParams.viewportY - 1
            const entity = this.client.clientGameLogic.playerEntity
            const tilemapMetaProvider = this.client.clientGameLogic.tilemapMetaProvider
            if (tilemapMetaProvider) {
                const location = entity.location
                const rightThreshold = (tilemapMetaProvider.width() - X_FACTOR) * TileSize
                const bottomThreshold = (tilemapMetaProvider.height() - Y_FACTOR) * TileSize

                const isOverX = location.x > TileSize * X_FACTOR && location.x > rightThreshold
                const isOverY = location.y > TileSize * Y_FACTOR && location.y > bottomThreshold

                if (this.pointer) {
                    const renderer: MapRenderingManager =
                        this.client.clientGameLogic.clientRenderer.map() as MapRenderingManager
                    const { modifierX, modifierY } = renderer.translationComputer
                    const factor = 0.5
                    const yfactor = 0.38
                    this.pointer = {
                        x:
                            Math.round(mouseState.position.x * factor) +
                            Math.round((modifierX !== undefined ? -modifierX : 0) * 1) -
                            (modifierX !== undefined
                                ? Math.round(TileSize * X_FACTOR * factor)
                                : isOverX
                                ? -rightThreshold + TileSize * X_FACTOR
                                : 0) -
                            (modifierX !== undefined ? 40 : -24),
                        y:
                            Math.round(mouseState.position.y * yfactor) +
                            Math.round((modifierY !== undefined ? -modifierY : 0) * 1) -
                            (modifierY !== undefined
                                ? Math.round(TileSize * Y_FACTOR * yfactor)
                                : isOverY
                                ? -bottomThreshold + TileSize * Y_FACTOR
                                : 0) -
                            (modifierY !== undefined ? 0 : -30),
                    }
                    this.pointer.y += -10
                    const newRoughPointerX = Math.floor(this.pointer.x / TileSize)
                    const newRoughPointerY = Math.floor(this.pointer.y / TileSize)
                    if (this.roughPointer.x !== newRoughPointerX || this.roughPointer.y !== newRoughPointerY) {
                        this.roughPointer.x = newRoughPointerX
                        this.roughPointer.y = newRoughPointerY
                        this.updatedRoughPointer(this.roughPointer)
                    }

                    this.entity.movement.angle = getAngle(this.pointer, entity.location)
                    this.targetKeyboardAngle = this.entity.movement.angle

                    // Keep an armed location pick's verdict current as the pointer travels: the
                    // reticle is amber over ground the NPC could stand on, red over ground it
                    // could not.
                    if (this.locationPick) {
                        this.client.clientGameLogic.clientRenderer
                            .gui()
                            .setLocationPickerBlocked(!this.locationPickable())
                    }

                    // ... and not the bow's crosshair on top of it: while a pick is armed the
                    // location reticle IS the pointer, and two of them read as a bug.
                    if ((this.longRangedEquipped || this.terrainPointerOn) && !this.locationPick) {
                        const mouseSprite = this.client.clientGameLogic.clientRenderer.gui().createCursorSprite()
                        mouseSprite.visible = true
                        mouseSprite.alpha = 1.0
                        if (this.mousePointerFadeInterval) {
                            clearInterval(this.mousePointerFadeInterval)
                        }
                        if (this.mousePointerFader) {
                            this.mousePointerFader.stop(true)
                            this.mousePointerFader = null
                        }
                        this.mousePointerFadeInterval = setTimeout(() => {
                            this.mousePointerFader = new Fader(mouseSprite, {
                                releaseCallback: () => {
                                    mouseSprite.visible = false
                                },
                            })
                            this.mousePointerFader.update()
                        }, 500)
                    }
                }
            }

            clearInterval(this.keyboardAndleAdjustInterval)
        }

        if (this.isButtonOn("0")) {
            // A click on an interaction icon (chest open/close, chat, etc.) must never also
            // trigger a world attack. The attack click arrives via a document-level mousedown
            // that can't tell it landed on a pixi interaction widget, so we suppress attacks
            // for a short window around every interaction activation (see suppressAttack).
            if (Date.now() < this.suppressAttackUntilTs) {
                return
            }

            // Check if command wheel is visible - if so, don't process mouse clicks for attacks
            if (this.client.clientGameLogic.clientRenderer.gui().isCommandWheelVisible()) {
                return
            }

            // Same for the name prompt: it stands open for as long as it takes to type a name, with
            // the player's attention on a field rather than the world, so a click meant for it (or
            // for the field it just lost focus to) must not fire a shot.
            if (this.client.clientGameLogic.clientRenderer.gui().isNamePromptVisible?.()) {
                return
            }

            // Likewise, a click on an inventory/crafting/trading screen (e.g. an item action
            // button) must not also fire a world attack. Those modals draw over the world but the
            // attack arrives via a document-level mousedown that can't see the pixi widget it hit,
            // so gate on the open screen the same way the command wheel does above.
            if (this.client.clientGameLogic.clientRenderer.gui().isItemScreenOpen()) {
                return
            }

            // button pressed
            if (!this.isFiring) {
                this.isFiring = true

                // An armed location pick claims this press: it is the player answering "click a
                // spot", not an attack on whatever happens to be under the reticle.
                if (this.locationPick) {
                    this.completeLocationPick()
                    return
                }

                // A press that landed on an entity offering interactions belongs to the press
                // arbitration instead: a short press activates the interaction, holding past the
                // threshold hands the press back here as an attack. The pixi pointer handlers on the
                // entity own it from this point (see beginEntityPress), so this click does nothing.
                if (this.pointedAtInteractableEntity()) {
                    return
                }

                this.startWorldAction()
            }
        } else if (this.isFiring) {
            // button unpressed
            mouseState.buttons.set("0", false)
            this.stopFiring()
        }
    }

    // Everything a plain press on the world does: commit a build tile, close on a distant target,
    // strike an adjacent entity tile, or fire. Extracted from respondToMouse so a press held on an
    // interactable entity can be handed back to it once the hold threshold passes.
    //
    // `ignoreHover` is what makes hold-to-attack actually swing at the thing under the pointer: with
    // a hovered entity this code would otherwise close on it and fire a bullet, because hovering has
    // always suppressed the melee strike. A deliberate hold on an adjacent entity means "hit that",
    // so the hover is dropped and the adjacent-tile strike path runs.
    private startWorldAction = (opts?: { ignoreHover?: boolean }) => {
        if (this.terrainPointerOn) {
            this.client.commitWorldEdits(this.roughPointer)
            this.playBuildSwing()
            this.clearTerrainPointer()

            this.fireInterval = setInterval(() => {
                this.client.commitWorldEdits(this.roughPointer)
                this.playBuildSwing()
            }, 400)

            this.lastMouseMoveTs = Date.now()
            return
        }

        const hoveredEntity = opts?.ignoreHover
            ? undefined
            : this.client.clientGameLogic.getEntity(this.hoveredEntity)
        let isAutoTravelling = false

        const entityTilePointer = !emptyCoordinate(this.roughCompositeTilePointer)
            ? this.roughCompositeTilePointer
            : this.roughPointer

        const entityTile = this.client.clientGameLogic.tilemapMetaProvider.getEntityTileAt(
            entityTilePointer.x,
            entityTilePointer.y,
        )
        const entityTileAccessible =
            entityTile &&
            getCoordinateDistance(this.entity.location, roughToFineCoordinates(entityTilePointer)) < TileSize

        if (!entityTileAccessible && !this.longRangedEquipped) {
            logger.info(
                { tags: ["callback", "animation"] },
                `Mouse click: Entity tile not accessible, not long-ranged. hoveredEntity=${!!hoveredEntity}`,
            )

            if (hoveredEntity) {
                logger.info(
                    { tags: ["callback", "animation"] },
                    `Mouse click: Scheduling auto-travel to hovered entity ${hoveredEntity.entityId}`,
                )
                isAutoTravelling = this.scheduleAutoTravelToEntity(hoveredEntity, () => {
                    logger.info(
                        { tags: ["callback", "animation"] },
                        `Mouse click: Firing bullet after reaching hovered entity`,
                    )
                    this.client.fireBullet()
                })
            } else {
                const collisionCheck = calculateNewCoordinateFromRadians(
                    this.entity.location,
                    this.entity.movement.angle,
                    TileSize,
                )
                if (
                    this.client.clientGameLogic.tilemapMetaProvider.isCollisionAt(
                        fineToRough(collisionCheck.x),
                        fineToRough(collisionCheck.y),
                    )
                ) {
                    logger.info(
                        { tags: ["callback", "animation"] },
                        `Mouse click: Collision detected, no auto-travel`,
                    )
                    isAutoTravelling = false
                } else {
                    const thePoint = calculatePointAtDistance(
                        this.entity.location,
                        this.pointer,
                        TileSize * 1.5,
                    )
                    logger.info(
                        { tags: ["callback", "animation"] },
                        `Mouse click: Scheduling auto-travel to calculated point (${thePoint.x}, ${thePoint.y})`,
                    )
                    isAutoTravelling = this.scheduleAutoTravelToPoint(
                        thePoint,
                        () => {
                            logger.info(
                                { tags: ["callback", "animation"] },
                                `Mouse click: Firing bullet after reaching calculated point`,
                            )
                            this.client.fireBullet()
                        },
                        false,
                    )
                }
            }
        }

        if (!isAutoTravelling) {
            if (entityTileAccessible && !hoveredEntity) {
                logger.info(
                    { tags: ["callback", "animation"] },
                    `Mouse click: Entity tile accessible, doing strike attack`,
                )
                const doStrike = () => {
                    const s = { ...this.roughCompositeTilePointer }
                    this.client.clientGameLogic.attack(this.entity.entityId, () => {
                        this.client.entityTileStrike(s)
                    })
                }

                doStrike()
                this.fireInterval = setInterval(() => {
                    doStrike()
                }, 400)
            } else {
                logger.info(
                    { tags: ["callback", "animation"] },
                    `Mouse click: No auto-travel, no accessible entity tile - firing bullet (no automatic boost - use Shift key for boost)`,
                )
                this.client.fireBullet()

                this.fireInterval = setInterval(() => {
                    logger.info(
                        { tags: ["callback", "animation"] },
                        `Mouse click: Repeating bullet fire in interval (no automatic boost)`,
                    )
                    this.client.fireBullet()
                }, 400)
            }
        }

        this.lastMouseMoveTs = Date.now()
    }

    // Stop an in-flight press: clears the repeat so a held attack can't outlive the press. Shared by
    // the mouse-up path and the entity press handlers, because a touch that is held never produces
    // the emulated mouseup a desktop release does.
    private stopFiring = () => {
        this.isFiring = false
        clearInterval(this.fireInterval)
        this.fireInterval = undefined
    }

    // Whether the pointer is currently on an entity that offers this player interactions. Read from
    // the client's own per-entity interaction cache, so it costs nothing and needs no round trip.
    private pointedAtInteractableEntity = (): boolean => this.interactionsFor(this.hoveredEntity).length > 0

    private interactionsFor = (entityId?: EntityId): InteractionDescriptor[] => {
        if (!entityId) {
            return []
        }
        return this.client.clientGameLogic.clientRenderer.entity().getEntityInteractions(entityId) || []
    }

    // ---- press arbitration ---------------------------------------------------------------------
    // One press on an entity, two meanings: tap to interact, hold to attack. Driven by the entity's
    // own pixi pointer events (see EntityRenderingManager.attachClickHandler) because those are the
    // only ones a finger and a mouse both produce. An entity with no interactions never gets here --
    // its press falls straight through to the world action, exactly as before.
    beginEntityPress = (entityId: EntityId) => {
        if (this.interactionsFor(entityId).length === 0) {
            return
        }
        // Build mode owns the press: placing a tile on the ground an interactable stands on must not
        // open the interactable instead.
        if (this.terrainPointerOn) {
            return
        }
        // Same for an armed location pick: "hold THIS spot" has to stay available even when an NPC
        // happens to be standing on the spot being chosen.
        if (this.locationPick) {
            return
        }
        const gui = this.client.clientGameLogic.clientRenderer.gui()
        // A wheel or picker already open sits directly over its entity, so a press on one of its
        // slices lands on the entity underneath too. The open widget owns that press.
        if (
            gui.isCommandWheelVisible() ||
            gui.isRolePickerVisible?.() ||
            gui.isNamePromptVisible?.() ||
            gui.isItemScreenOpen()
        ) {
            return
        }
        this.clearPressHoldTimer()
        this.pressedEntityId = entityId
        this.pressBecameAttack = false
        this.pressHoldTimer = setTimeout(() => {
            this.pressHoldTimer = undefined
            // Held long enough to mean it: this press is an attack on whatever is under the pointer.
            this.pressBecameAttack = true
            if (!this.isFiring) {
                this.isFiring = true
            }
            this.startWorldAction({ ignoreHover: true })
        }, ENTITY_PRESS_HOLD_MS)
    }

    endEntityPress = (entityId: EntityId) => {
        if (this.pressedEntityId !== entityId) {
            return
        }
        const becameAttack = this.pressBecameAttack
        this.releaseEntityPress()
        if (becameAttack) {
            // The hold already started swinging; releasing just stops it.
            this.stopFiring()
            return
        }
        this.activateEntityInteraction(entityId)
    }

    cancelEntityPress = (entityId: EntityId) => {
        if (this.pressedEntityId !== entityId) {
            return
        }
        const becameAttack = this.pressBecameAttack
        this.releaseEntityPress()
        if (becameAttack) {
            this.stopFiring()
        }
    }

    private releaseEntityPress = () => {
        this.clearPressHoldTimer()
        this.pressedEntityId = undefined
        this.pressBecameAttack = false
    }

    private clearPressHoldTimer = () => {
        if (this.pressHoldTimer) {
            clearTimeout(this.pressHoldTimer)
            this.pressHoldTimer = undefined
        }
    }

    // One interaction fires straight away; two or more (or a single wheel-only one, like handing over
    // food) open the wheel over the entity so the choice is deliberate. resolveInteractions holds
    // that contract, shared with the server-side defaults and the tests.
    private activateEntityInteraction = (entityId: EntityId) => {
        const resolution = resolveInteractions(this.interactionsFor(entityId))
        if (resolution.kind === "single") {
            this.client.clientGameLogic.interactionCallback(entityId, resolution.interaction.type)
            return
        }
        if (resolution.kind === "menu") {
            // The wheel itself gates clicks while it is open, but the press that opened it is still
            // travelling through the document-level mouse listeners.
            this.suppressAttack()
            this.client.clientGameLogic.clientRenderer.gui().showInteractionWheel(entityId, resolution.interactions)
        }
    }

    updatedRoughPointer = (coordinate: Coordinate) => {
        if (this.terrainPointerOn) {
            this.client.getBuildingTilePreview(coordinate)
        }
    }

    clearTerrainPointer = () => {
        this.terrainPointerSprites.forEach(sprite => sprite.parent?.removeChild(sprite))
        this.terrainPointerSprites.clear()
    }

    // Cosmetic feedback for placing a build tile: play the local player's attack (swing) animation.
    // Goes straight to the renderer's animation path (NOT clientGameLogic.attack, which would also
    // start a real server attack + audio), so there's no damage or gameplay effect. Other players
    // are shown the swing via the server's type_playAttackAnimation broadcast on the build commit.
    playBuildSwing = () => {
        if (this.entity) {
            this.client.clientGameLogic.clientRenderer.entity().attack(this.entity)
        }
    }

    updateTerrainPointer = (deltas: MapEntry[]) => {
        this.clearTerrainPointer()

        if (deltas?.length < 1) {
            return
        }

        deltas.forEach((delta: MapEntry) => {
            if (delta.layerId === "blocking" || delta.layerId === "skyTrigger") {
                return
            }
            const result = this.client.clientGameLogic.clientRenderer
                .map()
                .addTileAt(delta.x, delta.y, String(delta.tileSpec.tilePos), delta.layerId)
            if (result.sprite) {
                this.terrainPointerSprites.add(result.sprite)
                result.sprite.alpha = 0.5
                if (delta?.context?.highZ) {
                    result.sprite.zIndex = 100000
                }
            }
        })
    }

    enabledMouseAction = (enabled: boolean) => {
        this.mouseActionEnabled = enabled
        if (!enabled) {
            // Whatever took the world's mouse away (a modal, a dialog) also took the pointer the
            // pick was waiting on, and respondToMouse stops running -- a reticle left up would just
            // hang wherever it was.
            this.cancelLocationPick()
        }
    }

    // Briefly suppress click-to-attack (strike / ranged / build). Called right after an interaction
    // is activated so the same press — or a rapid follow-up tap on the same entity (open then close a
    // chest) — can't fall through to a world attack. Needed because the attack click comes from a
    // document-level mousedown, which can't see that the press was claimed by an entity.
    suppressAttack = (durationMs: number = 300) => {
        this.suppressAttackUntilTs = Date.now() + durationMs
    }

    setHoveredEntity = (entityId: EntityId) => {
        this.hoveredEntity = entityId
    }

    unHoveredEntity = (entityId: EntityId) => {
        // Only clear if this is still the hovered entity. When the pointer moves
        // straight from one entity to another, the new entity's mouseover can run
        // before the old entity's mouseout; an unconditional clear would then wipe the
        // freshly-set hover and break click auto-travel.
        if (this.hoveredEntity === entityId) {
            this.hoveredEntity = undefined
        }
    }

    // Called when a hovered entity is removed or culled out of range. Releases the hover reference
    // and re-enables world mouse actions so they can't stay stuck disabled if the entity vanished
    // mid-press, and drops any press being arbitrated against it -- an entity that no longer exists
    // will never send the pointer-up that would otherwise resolve or cancel its press.
    clearHoverForEntity = (entityId: EntityId) => {
        this.cancelEntityPress(entityId)
        if (this.hoveredEntity === entityId) {
            this.hoveredEntity = undefined
            this.mouseActionEnabled = true
        }
    }

    isMouseActionEnabled = () => this.mouseActionEnabled

    enabledKeyboardAction = (enabled: boolean) => {
        this.keyboardActionEnabled = enabled
    }

    paralyze = (value: boolean, timeout: number = 1000) => {
        this.paralyzed = value
        if (value) {
            setTimeout(() => {
                this.paralyzed = false
            }, timeout)
        }
    }

    // Mirrors the server's DOWNED verdict. Every world-state message for this player carries it,
    // so it clears itself the moment the server stops saying it -- there is no client-side timer
    // to get out of step with, and nothing here that can hold a player down on its own.
    setDowned = (value: boolean) => {
        if (value === this.downed) {
            return
        }
        this.downed = value
        if (value) {
            this.cancelLocationPick()
            const { entity } = this
            entity.movement.moving = false
            entity.speed2.x = 0
            entity.speed2.y = 0
            this.targetSpeed2 = { x: 0, y: 0 }
            this.joystickSpeed2 = null
            clearInterval(this.autoMovingInterval)
            this.setAutoMoving(false)
        }
    }

    fireBullet = () => {
        this.client.fireBullet()
    }

    selectSlot = (itemId: string) => {
        this.client.clientGameLogic.selectSlot(itemId)
    }

    activeAbility = (shortcutKey: string) => {
        this.client.activateAbility(shortcutKey)
    }

    isGuest = () => this.guest

    setIsGuest = (guest: boolean) => {
        this.guest = guest
    }

    updateLongRangedEquippedState = (equipped: boolean) => {
        if (this.longRangedEquipped !== equipped) {
            this.longRangedEquipped = equipped

            const mouseSprite = this.client.clientGameLogic.clientRenderer.gui().createCursorSprite()
            if (!equipped && mouseSprite) {
                mouseSprite.visible = false
            }
        }
    }

    updateTerrainPointerOn = (on: boolean) => {
        this.terrainPointerOn = on
        if (!this.terrainPointerOn) {
            this.clearTerrainPointer()
        }
    }

    // ---- location pick ---------------------------------------------------------------------------
    // "Click a spot on the ground, then do something with it." The defender's "Hold Position" order
    // is the first user: the wheel selects the order, this chooses the post, and only then does the
    // command go to the server (see GuiManager's onCommandSelect).
    //
    // The gesture deliberately sits at the same level as build mode rather than inside the command
    // wheel: both take the pointer away from attacking for one press, both are backed out of with
    // Escape, and both have to hold back the entity-press arbitration while they are armed.

    beginLocationPick = (request: LocationPickRequest) => {
        // Build mode and a location pick both own the pointer. The newer intent wins, and build mode
        // has to be ENDED server-side too, not just visually dropped (see the Escape path).
        if (this.terrainPointerOn) {
            this.updateTerrainPointerOn(false)
            this.client.clientGameLogic.cancelBuildPlacement()
        }
        // A press already being arbitrated against an entity would turn into an attack under the
        // reticle when its hold timer fired. Arming the pick ends it.
        this.releaseEntityPress()
        this.locationPick = request
        const gui = this.client.clientGameLogic.clientRenderer.gui()
        const cursorSprite = gui.createCursorSprite()
        if (cursorSprite) {
            cursorSprite.visible = false
        }
        gui.showLocationPicker(request.caption, request.blockedCaption)
        gui.setLocationPickerBlocked(!this.locationPickable())
        // The press that picked the order off the command wheel is still travelling through the
        // document-level mouse listeners (pixi's pointerdown on the wheel runs first). Without this
        // it would arrive here as the pick itself and post the NPC under the wheel slice.
        this.suppressAttack()
    }

    cancelLocationPick = () => {
        if (!this.locationPick) {
            return
        }
        this.locationPick = undefined
        this.client.clientGameLogic.clientRenderer.gui().hideLocationPicker()
    }

    isPickingLocation = (): boolean => !!this.locationPick

    // The fine world coordinate under the pointer. Asked of the GUI rather than read off
    // `this.pointer`: that one is hand-derived from the camera modifiers and lifted ~10px so a bow
    // aims at a body rather than at the ground beneath it, which is the wrong bias for "stand here".
    private locationPickTarget = (): Coordinate | null =>
        this.client.clientGameLogic.clientRenderer.gui().worldPointAtPointer()

    // Could an NPC actually stand where the pointer is? A post inside a wall would leave the NPC
    // walking into it forever, so blocked ground is refused at the click instead of being sent and
    // quietly ignored -- and the reticle says so in red before the player commits to it.
    private locationPickable = (): boolean => {
        const point = this.locationPickTarget()
        if (!point) {
            return false
        }
        const tilemap = this.client.clientGameLogic.tilemapMetaProvider
        if (!tilemap) {
            return false
        }
        const rough = fineToRoughCoordinates(point)
        if (rough.x < 0 || rough.y < 0 || rough.x >= tilemap.width() || rough.y >= tilemap.height()) {
            return false
        }
        return !tilemap.isCollisionAt(rough.x, rough.y)
    }

    private completeLocationPick = () => {
        const request = this.locationPick
        if (!request) {
            return
        }
        const point = this.locationPickTarget()
        if (!point || !this.locationPickable()) {
            // A click on ground nothing can stand on is not an answer. Stay armed so the player can
            // just click somewhere else -- the reticle is already red, so it isn't a silent refusal.
            return
        }
        this.cancelLocationPick()
        request.onPick(roundCoordinates(point))
    }

    builderActivation = (on: boolean) => {
        this.updateTerrainPointerOn(on)
        if (on) {
            const pointer =
                !this.roughPointer || this.roughPointer.x < 1 || this.roughPointer.y < 1
                    ? fineToRoughCoordinates(this.entity.location)
                    : this.roughPointer
            this.client.getBuildingTilePreview(pointer)
        }
    }

    manualAttack = (isMoving: boolean) => {
        logger.info({ tags: ["callback", "animation"] }, `manualAttack: Spacebar pressed, isMoving=${isMoving}`)

        const useMousePointer = Date.now() - this.lastMouseMoveTs < 150
        const collisionCheck = calculateNewCoordinateFromRadians(
            this.entity.location,
            this.entity.movement.angle,
            TileSize,
        )

        if (
            this.client.clientGameLogic.tilemapMetaProvider.isCollisionAt(
                fineToRough(collisionCheck.x),
                fineToRough(collisionCheck.y),
            )
        ) {
            logger.info({ tags: ["callback", "animation"] }, `manualAttack: Collision detected, firing bullet directly`)
            this.client.fireBullet()
            return
        }

        if (isMoving) {
            logger.info(
                { tags: ["callback", "animation"] },
                `manualAttack: Player is moving, firing bullet (no automatic boost - use Shift key for boost)`,
            )
            this.client.fireBullet()
            return
        }

        logger.info({ tags: ["callback", "animation"] }, `manualAttack: Player not moving, scheduling auto-travel`)

        const distance = this.longRangedEquipped ? 0 : TileSize * 1.5
        const thePoint = useMousePointer
            ? calculatePointAtDistance(this.entity.location, this.pointer, distance)
            : calculateNewCoordinateFromRadians(this.entity.location, this.entity.movement.angle, distance)

        this.scheduleAutoTravelToPoint(
            thePoint,
            () => {
                this.client.fireBullet()
            },
            false,
        )
    }

    setAutoMoving = (moving: boolean) => {
        this.isAutoMoving = moving
    }

    // The mobile joystick's held movement intent, as the same per-axis -1/0/1 vector a WASD
    // combination produces, or null when the thumb is off the stick. updatePlayerVelocity treats
    // it exactly like a held key. See GuiManager.setupJoystick for why it must be latched rather
    // than written straight onto entity.speed2.
    setJoystickIntent = (intent: Coordinate | null) => {
        const wasHeld = this.joystickSpeed2 !== null
        this.joystickSpeed2 = intent

        if (intent && !wasHeld) {
            // Started moving: take over from any auto-travel and start the same 700ms-then-inertia
            // ramp the keyboard path starts, so both input sources feel the same.
            clearInterval(this.autoMovingInterval)
            this.setAutoMoving(false)
            this.movementStartTime = Date.now()
            this.isSustainedMovement = false
        }
    }

    scheduleAutoTravelToEntity = (entity: Entity, travelCallback: Callback<void>) => {
        if (!entity) {
            return false
        }

        if (this.autoMovingInterval) {
            clearInterval(this.autoMovingInterval)
        }
        const { entity: playerEntity } = this

        const distance = getEntityDistance(playerEntity, entity)
        const tooClose = distance < 24
        const tooFar = distance > TileSize * 2

        if (!tooClose && !tooFar) {
            // console.log("distance", distance)
            this.autoTravelToEntity(entity, travelCallback)
            return true
        }

        return false
    }

    scheduleAutoTravelToPoint = (point: Coordinate, travelCallback: Callback<void>, restricted: boolean = true) => {
        if (!point) {
            return
        }

        if (this.autoMovingInterval) {
            clearInterval(this.autoMovingInterval)
        }
        const { entity: playerEntity } = this

        const distance = getDistance(point.x, point.y, playerEntity.location.x, playerEntity.location.y)
        const tooClose = distance < 24
        const tooFar = distance > TileSize * 2

        logger.debug(
            { tags: ["callback", "animation"] },
            `scheduleAutoTravelToPoint: distance=${distance}, tooClose=${tooClose}, tooFar=${tooFar}, restricted=${restricted}`,
        )

        if (!restricted || (!tooClose && !tooFar)) {
            logger.info(
                { tags: ["callback", "animation"] },
                `Starting auto-travel to point (${point.x}, ${point.y}) from (${playerEntity.location.x}, ${playerEntity.location.y})`,
            )
            this.autoTravelTo(point, travelCallback)
            return true
        }

        logger.debug({ tags: ["callback", "animation"] }, `Auto-travel blocked: tooClose=${tooClose}, tooFar=${tooFar}`)
        return false
    }

    autoTravelToEntity = (entity: Entity, arriveCallback?: Callback<void>) => {
        const targetCoordinate: Coordinate = floorCoordinates(entity.location)
        return this.autoTravelTo(targetCoordinate, arriveCallback)
    }

    autoTravelTo = (targetCoordinate: Coordinate, arriveCallback?: Callback<void>) => {
        const { entity: playerEntity } = this
        this.setAutoMoving(true)
        this.client.boost(true)

        logger.info(
            { tags: ["callback", "animation"] },
            `autoTravelTo: Starting travel to (${targetCoordinate.x}, ${targetCoordinate.y}) from (${playerEntity.location.x}, ${playerEntity.location.y}) - CALLBACK EXECUTED IMMEDIATELY`,
        )

        // BUG: This callback is executed immediately, not when arriving!
        arriveCallback()

        let startTs = Date.now()
        this.autoMovingInterval = setInterval(() => {
            const distance = Math.floor(getCoordinateDistance(playerEntity.location, targetCoordinate))
            const tooClose = distance < 24
            const tooFar = distance > TileSize * 2

            const isThere = isEqual(targetCoordinate, playerEntity.location)
            if (tooClose || isThere || tooFar) {
                logger.info(
                    { tags: ["callback", "animation"] },
                    `autoTravelTo: Arrived at destination! distance=${distance}, tooClose=${tooClose}, tooFar=${tooFar}, isThere=${isThere}`,
                )
                clearInterval(this.autoMovingInterval)
                this.setAutoMoving(false)

                this.targetSpeed2 = { x: 0, y: 0 }
                this.client.boost(false)

                return
            }

            if (Date.now() - startTs < 200) {
                return
            }

            // Set target speed2 for auto-travel
            this.targetSpeed2 = { x: 0, y: 0 }

            if (Math.floor(playerEntity.location.x) > targetCoordinate.x) {
                this.targetSpeed2.x = -1
            } else if (Math.floor(playerEntity.location.x) < targetCoordinate.x) {
                this.targetSpeed2.x = 1
            }

            if (Math.floor(playerEntity.location.y) > targetCoordinate.y) {
                this.targetSpeed2.y = -1
            } else if (Math.floor(playerEntity.location.y) < targetCoordinate.y) {
                this.targetSpeed2.y = 1
            }
        }, 5)
    }

    updateCompositeTile = (x: number, y: number) => {
        const entityTile = this.client.clientGameLogic.tilemapMetaProvider.getEntityTileAt(
            this.roughPointer.x,
            this.roughPointer.y,
        )
        if (entityTile && x > -1 && y > -1) {
            this.roughCompositeTilePointer.x = x
            this.roughCompositeTilePointer.y = y
        } else {
            clearCoordinate(this.roughCompositeTilePointer)
        }
    }

    startVelocityUpdates = () => {
        const updateInterval = 1000 / MovementInertiaConfig.UPDATE_FPS
        this.velocityUpdateInterval = setInterval(() => {
            this.updatePlayerVelocity()
        }, updateInterval)
    }

    stopVelocityUpdates = () => {
        if (this.velocityUpdateInterval) {
            clearInterval(this.velocityUpdateInterval)
            this.velocityUpdateInterval = null
        }
    }

    updatePlayerVelocity = () => {
        if (!this.entity || !MovementInertiaConfig.ENABLED) return

        // Stop movement if player is dead, downed or paralyzed. This loop OWNS entity.speed2, so
        // it is the one gate every kind of movement intent passes through -- held keys, a parked
        // joystick thumb, an auto-travel in progress.
        if (this.entity.isDead() || this.paralyzed || this.downed) {
            this.entity.speed2.x = 0
            this.entity.speed2.y = 0
            this.entity.movement.moving = false
            return
        }

        const friction = MovementInertiaConfig.FRICTION
        const minThreshold = MovementInertiaConfig.MIN_VELOCITY_THRESHOLD
        const acceleration = MovementInertiaConfig.ACCELERATION

        // Check if any keys are pressed
        const keyboardMoving =
            this.isKeyOn("w") ||
            this.isKeyOn("ArrowUp") ||
            this.isKeyOn("s") ||
            this.isKeyOn("ArrowDown") ||
            this.isKeyOn("a") ||
            this.isKeyOn("ArrowLeft") ||
            this.isKeyOn("d") ||
            this.isKeyOn("ArrowRight")

        // A held joystick is a held input too, and it owns the target vector while it is down:
        // its onChange fires only while the thumb actually moves, so nothing else would keep
        // targetSpeed2 current. The keyboard wins if somehow both are active.
        const joystick = this.joystickSpeed2
        const isMoving = keyboardMoving || joystick !== null
        if (joystick && !keyboardMoving) {
            this.targetSpeed2 = this.entity.afk ? { x: 0, y: 0 } : { x: joystick.x, y: joystick.y }
        }

        // Check if we've been moving for more than 1 second
        const movementDuration = this.movementStartTime > 0 ? Date.now() - this.movementStartTime : 0
        const shouldUseInertia = movementDuration > 700

        if (isMoving) {
            // Update sustained movement state
            if (shouldUseInertia && !this.isSustainedMovement) {
                this.isSustainedMovement = true
            }

            if (shouldUseInertia) {
                // Keys are pressed and we've been moving for >1s - smoothly interpolate toward target
                const diffX = this.targetSpeed2.x - this.entity.speed2.x
                const diffY = this.targetSpeed2.y - this.entity.speed2.y

                this.entity.speed2.x += diffX * acceleration
                this.entity.speed2.y += diffY * acceleration
            } else {
                // Keys are pressed but <1s - immediate response
                this.entity.speed2.x = this.targetSpeed2.x
                this.entity.speed2.y = this.targetSpeed2.y
            }
        } else {
            // No keys pressed
            if (shouldUseInertia && this.isSustainedMovement) {
                // Apply friction to slow down only if we were in sustained movement
                this.entity.speed2.x *= friction
                this.entity.speed2.y *= friction

                // Stop movement if below threshold
                const speed = Math.sqrt(
                    this.entity.speed2.x * this.entity.speed2.x + this.entity.speed2.y * this.entity.speed2.y,
                )
                if (speed < minThreshold) {
                    this.entity.speed2.x = 0
                    this.entity.speed2.y = 0
                }
            } else {
                // Immediate stop for short movements
                this.entity.speed2.x = 0
                this.entity.speed2.y = 0
            }
        }

        // Update movement state
        const speed = Math.sqrt(
            this.entity.speed2.x * this.entity.speed2.x + this.entity.speed2.y * this.entity.speed2.y,
        )
        this.entity.movement.moving = speed > minThreshold
    }

    destroy = () => {
        // Hand the input back. A reconnect builds a NEW Player (ClientGameLogic.initializePlayer)
        // and the keyboard's listener list is static, so a controller that leaves its listeners
        // behind has every keystroke answered twice from then on -- which reads, in chat, as every
        // character you type appearing twice.
        this.keyboardHandler.removeListener(this.respondToKeyBoard)
        this.keyboardHandler.removePasteListener(this.respondToPaste)
        this.mouseHandler.removeListener(this.respondToMouse)
        this.stopVelocityUpdates()
        if (this.movementDaemon) {
            clearInterval(this.movementDaemon)
        }
        if (this.autoMovingInterval) {
            clearInterval(this.autoMovingInterval)
        }
        if (this.keyboardAndleAdjustInterval) {
            clearInterval(this.keyboardAndleAdjustInterval)
        }
        if (this.fireInterval) {
            clearInterval(this.fireInterval)
        }
        if (this.mousePointerFadeInterval) {
            clearTimeout(this.mousePointerFadeInterval)
        }
    }
}
